/**
 * MetaCampaignElementsList Component
 *
 * Meta analog of `CampaignElementsList`. Both components share:
 *  - `useOptimisticEntityUpdates` for local state + store wiring +
 *    discard callback + handleAction
 *  - `<EntityTreeList>` for the parent/child tree rendering
 *
 * Only the Meta-specific config (vocab, valid actions, hidden statuses,
 * status badge classes, normalizer, ConfirmDialog wording) lives here.
 *
 *   Google                          Meta
 *   ──────                          ────
 *   entityType: 'ad_group' | 'ad'   entityType: 'ad_set' | 'ad'
 *   action: 'pause'|'enable'|       action: 'pause'|'enable'|'archive'
 *           'remove'
 *   status: ENABLED|PAUSED|REMOVED  status: ACTIVE|PAUSED|ARCHIVED|DELETED
 *   platform: 'google_ads'          platform: 'meta_ads'
 */

import { useCallback, useMemo } from 'react';
import type { Campaign } from '@/types/campaign';
import type { KebabMenuOption } from '@/components/ui/KebabMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useOptimisticEntityUpdates } from '@/hooks/useOptimisticEntityUpdates';
import {
  EntityTreeList,
  type GetMenuOptionsParams,
  type NormalizedTreeChild,
  type NormalizedTreeParent,
} from './EntityTreeList';

type MetaAction = 'pause' | 'enable' | 'archive';
type MetaEntityType = 'ad_set' | 'ad';

interface MetaCampaignElementsListProps {
  campaign: Campaign;
  onElementUpdated: () => void;
  onEntityArchived?: (entityId: string) => void;
}

const META_VALID_ACTIONS: readonly MetaAction[] = ['pause', 'enable', 'archive'];
const META_HIDDEN_STATUSES: readonly string[] = ['ARCHIVED', 'DELETED'];

function formatMetaToastSubject(type: MetaEntityType): string {
  return type === 'ad_set' ? 'ad set' : 'ad';
}

function formatStatusLabel(status: string): string {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

// Meta-vocab badge color table. ACTIVE shares Google's green;
// ARCHIVED/DELETED are gray (restorable terminal state Meta allows
// rather than the destructive REMOVED), matching `CampaignStatusBadge`
// from `CampaignCard/shared`.
function getMetaStatusBadgeClasses(status: string): string {
  const s = (status ?? '').toUpperCase();
  if (s === 'ENABLING') return 'bg-green-100 text-green-800 animate-pulse';
  if (s === 'PAUSING') return 'bg-amber-100 text-amber-800 animate-pulse';
  if (s === 'ARCHIVING') return 'bg-gray-100 text-gray-700 animate-pulse';
  if (s === 'ACTIVE' || s === 'ENABLED') return 'bg-green-100 text-green-800';
  if (s === 'PAUSED') return 'bg-amber-100 text-amber-800';
  if (s === 'ARCHIVED' || s === 'DELETED') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-800';
}

export function MetaCampaignElementsList({
  campaign,
  onElementUpdated,
  onEntityArchived,
}: MetaCampaignElementsListProps) {
  const {
    hasPendingChange,
    getPendingAction,
    getEffectiveStatus,
    isHidden,
    handleAction,
    pendingAction,
    setPendingAction,
    confirmPendingAction,
  } = useOptimisticEntityUpdates<MetaAction, MetaEntityType>({
    campaignId: campaign.campaign_id,
    platform: 'meta_ads',
    validActions: META_VALID_ACTIONS,
    terminalAction: 'archive',
    activeStatus: 'ACTIVE',
    pausedStatus: 'PAUSED',
    hiddenStatuses: META_HIDDEN_STATUSES,
    discardCallbackKeyPrefix: 'meta-campaign-elements',
    onElementUpdated,
    onTerminalAction: onEntityArchived,
    formatToastSubject: formatMetaToastSubject,
  });

  // Normalize Meta ad sets + ads into NormalizedTreeParent[].
  const normalizedAdSets = useMemo<NormalizedTreeParent[]>(() => {
    return (campaign.ad_sets ?? [])
      .filter((as) => !isHidden(as.ad_set_id, as.status ?? 'ACTIVE'))
      .map((as) => {
        const adSetName = as.ad_set_name ?? as.ad_set_id;
        const adSetStatus = as.status ?? 'ACTIVE';
        const children: NormalizedTreeChild[] = (as.ads ?? [])
          .filter((ad) => !isHidden(ad.ad_id, ad.status ?? 'ACTIVE'))
          .map((ad) => ({
            id: ad.ad_id,
            name: ad.ad_name ?? `Ad ${ad.ad_id}`,
            status: ad.status ?? 'ACTIVE',
            entityType: 'ad',
            parentId: as.ad_set_id,
            typeLabel: 'Ad',
          }));
        return {
          id: as.ad_set_id,
          name: adSetName,
          status: adSetStatus,
          entityType: 'ad_set',
          children,
        };
      });
  }, [campaign.ad_sets, isHidden]);

  const StatusBadge = useCallback(
    ({ status, entityId }: { status: string; entityId: string }) => {
      const effectiveStatus = getEffectiveStatus(entityId, status).toUpperCase();
      const isPending = hasPendingChange(entityId);
      const pendingActionType = getPendingAction(entityId);

      let displayStatus = effectiveStatus;
      let displayLabel = formatStatusLabel(effectiveStatus);

      if (isPending && pendingActionType) {
        if (pendingActionType === 'enable') {
          displayStatus = 'ENABLING';
          displayLabel = 'Enabling…';
        } else if (pendingActionType === 'pause') {
          displayStatus = 'PAUSING';
          displayLabel = 'Pausing…';
        } else if (pendingActionType === 'archive') {
          displayStatus = 'ARCHIVING';
          displayLabel = 'Archiving…';
        }
      }

      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMetaStatusBadgeClasses(displayStatus)}`}
        >
          {displayLabel}
        </span>
      );
    },
    [getEffectiveStatus, hasPendingChange, getPendingAction],
  );

  const getMenuOptions = useCallback(
    ({ type, entityId, entityName, status, parentId }: GetMenuOptionsParams): KebabMenuOption[] => {
      const metaType = type as MetaEntityType;
      const effectiveStatus = getEffectiveStatus(entityId, status).toUpperCase();
      const isPending = hasPendingChange(entityId);
      const options: KebabMenuOption[] = [];

      if (effectiveStatus === 'ACTIVE') {
        options.push({
          label: 'Pause',
          onClick: () => handleAction(metaType, entityId, 'pause', entityName, parentId),
          disabled: isPending,
        });
      } else if (effectiveStatus === 'PAUSED') {
        options.push({
          label: 'Enable',
          onClick: () => handleAction(metaType, entityId, 'enable', entityName, parentId),
          variant: 'success',
          disabled: isPending,
        });
      }

      options.push({
        label: 'Archive',
        onClick: () =>
          setPendingAction({
            type: metaType,
            action: 'archive',
            entityId,
            entityName,
            adGroupId: parentId,
          }),
        variant: 'danger',
        disabled: isPending,
      });

      return options;
    },
    [getEffectiveStatus, hasPendingChange, handleAction, setPendingAction],
  );

  if (normalizedAdSets.length === 0) return null;

  return (
    <>
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Ad Sets & Ads</h4>

        <EntityTreeList
          parents={normalizedAdSets}
          StatusBadge={StatusBadge}
          getMenuOptions={getMenuOptions}
        />
      </div>

      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
        title={`Archive ${pendingAction?.type === 'ad_set' ? 'Ad Set' : 'Ad'}?`}
        message={`Are you sure you want to archive "${pendingAction?.entityName}"? This queues the change — click Save Changes to apply. Archived items can be restored from Meta Ads Manager.`}
        confirmText="Archive"
        cancelText="Cancel"
        variant="warning"
      />
    </>
  );
}

export default MetaCampaignElementsList;
